/**
 * Self-contained sign-in / sign-up form.
 *
 * Factored out of SignInDialog so it can be rendered inline (e.g. inside the
 * TableScanInterstitial) without the modal chrome. State is local — no
 * dependency on SignInDialogContext.
 *
 * Pulls auth methods from AuthActionsContext (same path the dialog uses), so
 * the AuthProvider's onAuthStateChanged side effects (login-event ping,
 * profile fetch, etc.) still fire normally.
 */

import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { AuthActionsContext, useAuth } from "@/lib/platform/AuthProvider";
import {
  consumeRedirectSignIn,
  isRedirectUnsupported,
} from "@/lib/platform/authRedirect";
import { getFirebaseAuth, isAuthHandlerSameOrigin } from "@/lib/firebase";
import { useT } from "@/lib/i18n";
import { useGoogleRedirectSignIn, useEmailCodeSignIn } from "@/lib/features";
import { api } from "@/lib/api";
import { extractEmailCode } from "@/lib/emailCode";

/** Verify failure reasons with localized copy, keyed by the server's `reason`. */
const VERIFY_ERROR_KEYS: Record<string, string> = {
  invalid_code: "auth.emailCode.invalidCode",
  expired: "auth.emailCode.expired",
  too_many_attempts: "auth.emailCode.tooManyAttempts",
};

export type SignInFormView =
  // The provider buttons (Apple / Google / email). In the code arm this sits
  // behind "other sign-in options"; in control it's still the landing view.
  | "main"
  | "signin-password"
  | "signup-password"
  | "reset-password"
  | "account-not-found"
  // One-time email code path (email_code_auth experiment): redeem the 6-digit
  // code, then — only when the code turned out to create a brand-new account on
  // a sign-in-flavored surface — collect a first name after the fact.
  | "email-code-verify"
  | "email-code-name";
// "email_code" is the one-time code path (email_code_auth experiment); "email"
// stays the password path, so the two read as separate rows in the auth funnel.
export type AuthProvider = "apple" | "google" | "email" | "email_code";

interface SignInFormProps {
  /** Controlled view — when supplied alongside `onViewChange`, view state is
   *  driven from the parent (used by SignInDialog so AuthProvider can flip
   *  the form to "account-not-found" via context). Otherwise the form keeps
   *  its own view state seeded from `initialView`. */
  view?: SignInFormView;
  onViewChange?: (next: SignInFormView) => void;
  /** Initial view in uncontrolled mode. Defaults to "main". */
  initialView?: SignInFormView;
  /**
   * Whether the email path is sign-up (collects name + confirm password) or
   * sign-in (email + password). Ignored when the view is forced to a
   * specific password view via `view` / `initialView`.
   */
  asSignUp?: boolean;
  /** Optional title above the form. Omit for a chromeless embed. */
  title?: string;
  /** Optional subtitle. */
  subtitle?: string;
  /** Allow the user to toggle between sign-up and sign-in inside the form. */
  allowSwitch?: boolean;
  /** Called after a successful sign-in or sign-up returns a user. */
  onSuccess?: (provider: AuthProvider) => void;
  /** Fired when the user taps any auth button — useful for tracking. */
  onAuthClick?: (provider: AuthProvider) => void;
  /** Fired when an OAuth attempt resolves without a user (cancel/failure).
   *  `errorCode` is the Firebase error code when one was thrown, else null. */
  onAuthFail?: (provider: AuthProvider, errorCode?: string | null) => void;
  /** Account-not-found "Create profile" tap — typically closes the parent
   *  surface so ProtectedRoute can redirect to /quiz. */
  onAccountNotFoundCreate?: () => void;
  /** "Sign up" tap under the code arm's no-account error. Omit it and the
   *  error shows alone, as on surfaces that already sign people up. */
  onSignUpInstead?: () => void;
  /**
   * Which kind of surface this is, for the redirect arm. The dialog's success
   * handling has to be reconstructed by AuthProvider on the far side of the
   * redirect, and an inline form's must not be — see `AuthRedirectIntent`.
   * Defaults to inline, which is what every embedded use of this form is.
   */
  redirectSurface?: "dialog" | "inline";
  /**
   * Where a redirect sign-in should land, when success means going somewhere
   * other than back to this page. Mirrors `SignInOptions.returnTo`.
   */
  redirectReturnTo?: string;
  /** Optional outer container class. */
  className?: string;
}

export function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function SignInForm({
  view: controlledView,
  onViewChange,
  initialView = "main",
  asSignUp = true,
  title,
  subtitle,
  allowSwitch = true,
  onSuccess,
  onAuthClick,
  onAuthFail,
  onAccountNotFoundCreate,
  onSignUpInstead,
  redirectSurface = "inline",
  redirectReturnTo,
  className,
}: SignInFormProps) {
  const { t } = useT();
  const actions = useContext(AuthActionsContext);
  const { signOut } = useAuth();

  const [internalView, setInternalView] = useState<SignInFormView>(initialView);
  const view = controlledView ?? internalView;
  const setView = (next: SignInFormView) => {
    if (onViewChange) onViewChange(next);
    if (controlledView === undefined) setInternalView(next);
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [oauthPending, setOauthPending] = useState<"apple" | "google" | null>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // ---- email_code_auth state ----
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  // Seconds until "send a new code" re-enables. Mirrors the server's own resend
  // cooldown so the button doesn't offer an action that would 429.
  const [resendIn, setResendIn] = useState(0);
  const [codeResent, setCodeResent] = useState(false);
  // Address the last code went to. Lets "email me a code instead" return to
  // that code rather than request another inside the resend cooldown.
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  // In the code arm the email step sits in front of the provider buttons, and
  // "other sign-in options" reveals them. Tracked separately from `view` so the
  // provider form itself stays exactly what it always was — and so control,
  // where this is always false, renders it untouched as the landing view.
  const [showAllOptions, setShowAllOptions] = useState(false);

  // email_code_auth experiment: the email path mails a one-time code instead of
  // asking for a password. Both arms keep the other path one tap away, so
  // existing password accounts are never stranded.
  const emailCodeFlagOn = useEmailCodeSignIn();
  /** Whether "main" renders the email step rather than the provider buttons. */
  const showEmailCodeStep = emailCodeFlagOn && !showAllOptions;

  // google_auth_redirect experiment: send the whole page to Google instead of
  // opening a popup the mobile browser may never honour. Every surface, since
  // each one either rebuilds itself from disk on return (the quiz gates, the
  // onboarding flow) or hands its success handling to AuthProvider via the
  // recorded intent (the dialog).
  //
  // Two ways back to the popup. `isAuthHandlerSameOrigin` is the precondition
  // and is known before anything is attempted — http dev serves the handler
  // from another origin, where the round trip always returns empty, so there
  // is nothing to learn by trying. `isRedirectUnsupported` is what we learn
  // afterwards, from a browser that refused a redirect we had every reason to
  // expect would work.
  const redirectFlagOn = useGoogleRedirectSignIn();
  const useRedirect =
    redirectFlagOn && isAuthHandlerSameOrigin() && !isRedirectUnsupported();

  // Firebase's `signInWithPopup` doesn't always reject when the user closes
  // the popup manually (depends on browser, cookie policy, popup blocker).
  // Without this, the spinner can stay on the button forever after a cancel.
  // Watchdog: while OAuth is pending, listen for the parent window regaining
  // focus (i.e. the popup just closed). After a short grace period — to let
  // Firebase resolve naturally if it's going to — clear the pending state.
  useEffect(() => {
    if (!oauthPending) return;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const onFocus = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setOauthPending(null), 500);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [oauthPending]);

  // Resend cooldown tick. Only runs while a countdown is live, so the form
  // doesn't hold a timer open for its whole lifetime.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  // The surface's own callbacks, reachable from the redirect effect below
  // without making it re-run — and re-fire — every time a parent re-renders.
  const onSuccessRef = useRef(onSuccess);
  const onAuthFailRef = useRef(onAuthFail);
  onSuccessRef.current = onSuccess;
  onAuthFailRef.current = onAuthFail;

  // Coming back from a redirect sign-in. The tap happened on a page that no
  // longer exists, so nothing else is left to tell this surface it succeeded:
  // the outcome is read from disk here and reported as if the round trip had
  // been a popup. Resolves null on every load that wasn't a redirect return,
  // which is nearly all of them.
  //
  // Inline surfaces only. A dialog's completion is AuthProvider's to run from
  // the recorded intent, and this form is mounted inside the dialog it may have
  // just reopened on the account-not-found step — reporting success there would
  // close the very view that was put up to ask the user something.
  const redirectOutcomeReadRef = useRef(false);
  useEffect(() => {
    if (redirectSurface !== "inline") return;
    if (redirectOutcomeReadRef.current) return;
    redirectOutcomeReadRef.current = true;
    let cancelled = false;
    void consumeRedirectSignIn(getFirebaseAuth()).then((outcome) => {
      if (cancelled || !outcome) return;
      if (outcome.user) onSuccessRef.current?.("google");
      else onAuthFailRef.current?.("google", outcome.errorCode);
    });
    return () => {
      cancelled = true;
    };
  }, [redirectSurface]);

  const [emailError, setEmailError] = useState<string | null>(null);
  // The code request found no account for the address, so the email step
  // offers a way to sign up next to the error.
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  if (!actions) {
    console.warn("[SignInForm] No AuthActionsContext — auth methods unavailable.");
    return null;
  }

  const {
    signInWithApple,
    signInWithGoogle,
    signInWithGoogleRedirect,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    requestEmailCode,
    signInWithEmailCode,
    getLastAuthErrorCode,
    isLoading,
    error,
    clearError,
  } = actions;

  const handleApple = async () => {
    if (oauthPending) return;
    onAuthClick?.("apple");
    setOauthPending("apple");
    try {
      const user = await signInWithApple();
      if (user) onSuccess?.("apple");
      else onAuthFail?.("apple", getLastAuthErrorCode());
    } finally {
      setOauthPending(null);
    }
  };

  const handleGoogle = async () => {
    if (oauthPending) return;
    onAuthClick?.("google");
    setOauthPending("google");

    if (useRedirect) {
      // Nothing to await: on the ordinary path this page is gone before the
      // call comes back, and the result is picked up by the effect above on
      // the load that returns. The spinner is deliberately left running so the
      // button doesn't flash back to normal on its way off the screen.
      await signInWithGoogleRedirect({
        surface: redirectSurface,
        returnTo: redirectReturnTo,
        asSignUp,
      });
      // Still here means the navigation was refused, and AuthProvider has put
      // a message on `error`. Hand the button back — but late enough that an
      // unload already in flight doesn't get a flicker out of it.
      setTimeout(() => setOauthPending(null), 3000);
      return;
    }

    try {
      const user = await signInWithGoogle();
      if (user) onSuccess?.("google");
      else onAuthFail?.("google", getLastAuthErrorCode());
    } finally {
      setOauthPending(null);
    }
  };

  const handleEmailEntry = () => {
    onAuthClick?.("email");
    setView(asSignUp ? "signup-password" : "signin-password");
  };

  // ---- One-time email code -------------------------------------------------

  const handleRequestEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendEmailCode(false);
  };

  /**
   * Sends (or resends) the code. `isResend` keeps the user on the code step and
   * shows a confirmation instead of re-animating the whole view.
   */
  const sendEmailCode = async (isResend = false) => {
    // Counted here rather than in the submit handler so the "use a code
    // instead" escape hatch reports the same way.
    if (!isResend) onAuthClick?.("email_code");
    clearAllErrors();
    setCodeResent(false);
    if (!email.trim()) return setEmailError(t("auth.errors.enterEmail"));
    if (!isValidEmail(email)) return setEmailError(t("auth.errors.invalidEmail"));
    // On a sign-up surface the first name is collected up front so it can be
    // sent with the code, which lets the server name the account at creation.
    if (asSignUp && !isResend && !firstName.trim()) {
      return setNameError(t("auth.errors.enterFirstName"));
    }

    const result = await requestEmailCode(email, asSignUp);
    if (result === "account-not-found") {
      onAuthFail?.("email_code", "account_not_found");
      setAccountNotFound(true);
      // Sits on the email field, next to the address that's wrong, rather than
      // in the generic error block at the foot of the form.
      return setEmailError(t("auth.errors.userNotFound"));
    }
    if (result === "rate-limited") {
      onAuthFail?.("email_code", "rate_limited");
      const message = t("auth.emailCode.rateLimited");
      return view === "email-code-verify" ? setCodeError(message) : setEmailError(message);
    }
    if (result !== "sent") {
      onAuthFail?.("email_code", "send_failed");
      return;
    }

    setCode("");
    setResendIn(30);
    setCodeSentTo(email.trim().toLowerCase());
    if (isResend) setCodeResent(true);
    else setView("email-code-verify");
  };

  const handleVerifyEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAllErrors();
    setCodeResent(false);

    const trimmed = code.trim();
    if (!trimmed) return setCodeError(t("auth.emailCode.enterCode"));
    if (!/^\d{6}$/.test(trimmed)) return setCodeError(t("auth.emailCode.codeLength"));

    const user = await signInWithEmailCode(
      email,
      trimmed,
      asSignUp && firstName.trim() ? firstName.trim() : undefined,
      asSignUp && lastName.trim() ? lastName.trim() : undefined,
    );
    if (!user) {
      // Per attempt, so a mistyped code that is then corrected logs a failure
      // and a success.
      const reason = getLastAuthErrorCode() ?? "verify_failed";
      onAuthFail?.("email_code", reason);
      // The server's copy is English-only; show ours for the reasons we know.
      // Anything else keeps the provider `error` rendered below the form.
      const key = VERIFY_ERROR_KEYS[reason];
      if (key) {
        clearError();
        setCodeError(t(key));
      }
      return;
    }

    // A sign-in surface can still land on a brand-new address, in which case
    // nobody has given us a name yet. Firebase reports the display name it was
    // created with, so an empty one means we owe the user a name step before
    // handing off to the app.
    if (!user.firstName) {
      setView("email-code-name");
      return;
    }
    onSuccess?.("email_code");
  };

  /**
   * Names an account that was created without one. The users row is written
   * lazily by GET /user/profile, so read it first: POST /user/profile is an
   * update and would silently no-op against a row that doesn't exist yet.
   */
  const handleSubmitCodeName = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAllErrors();
    if (!firstName.trim()) return setNameError(t("auth.errors.enterFirstName"));

    try {
      await api.get("/user/profile");
      await api.post("/user/profile", {
        firstName: firstName.trim(),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      });
    } catch (err) {
      // The session is already live at this point, so a naming failure must not
      // trap the user on this step: let them through and edit it in settings.
      console.error("[SignInForm] Failed to save name after code sign-in:", err);
    }
    onSuccess?.("email_code");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAllErrors();
    if (!email.trim()) return setEmailError(t("auth.errors.enterEmail"));
    if (!isValidEmail(email)) return setEmailError(t("auth.errors.invalidEmail"));
    await resetPassword(email);
    setResetEmailSent(true);
  };

  // Account-not-found "Try other account" — sign user out, reset local form
  // state, drop them back to the welcome view so they can pick a new flow.
  const handleAccountNotFoundTryOther = async () => {
    await signOut();
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setResetEmailSent(false);
    clearAllErrors();
    setShowAllOptions(false);
    setView("main");
  };

  const clearAllErrors = () => {
    setEmailError(null);
    setAccountNotFound(false);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setNameError(null);
    setCodeError(null);
    clearError();
  };

  const switchView = (next: SignInFormView) => {
    setView(next);
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setCode("");
    setCodeResent(false);
    clearAllErrors();
  };

  /**
   * Escape hatch between the code and password arms of the email path. Going
   * back to the code arm returns to the code already sent to this address, or
   * mails one straight away rather than bouncing through "main" — the address
   * is already in hand by the time this is reachable.
   */
  const switchEmailMethod = async (next: "code" | "password") => {
    setCode("");
    setCodeResent(false);
    setPassword("");
    setConfirmPassword("");
    clearAllErrors();

    if (next === "password") {
      setView(asSignUp ? "signup-password" : "signin-password");
      return;
    }
    // Choosing the code path puts the email step back in front, so a later
    // "back" lands there rather than on the provider buttons.
    setShowAllOptions(false);
    // A code already went to this address: go back to entering it. Expired
    // or not, the code step offers "send a new code" once the cooldown ends.
    if (codeSentTo && codeSentTo === email.trim().toLowerCase()) {
      setView("email-code-verify");
      return;
    }
    if (isValidEmail(email)) {
      await sendEmailCode(false);
      return;
    }
    setView("main");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAllErrors();

    if (!email.trim()) return setEmailError(t("auth.errors.enterEmail"));
    if (!isValidEmail(email)) return setEmailError(t("auth.errors.invalidEmail"));
    if (!password.trim()) return setPasswordError(t("auth.errors.enterPassword"));

    if (view === "signup-password") {
      if (!firstName.trim()) return setNameError(t("auth.errors.enterFirstName"));
      if (password.length < 6) return setPasswordError(t("auth.errors.passwordMinLength"));
      if (password !== confirmPassword) {
        return setConfirmPasswordError(t("auth.errors.passwordMismatch"));
      }
      const user = await signUpWithEmail(email, password, firstName, lastName);
      if (user) onSuccess?.("email");
    } else {
      const user = await signInWithEmail(email, password);
      if (user) onSuccess?.("email");
    }
  };

  return (
    <div className={className}>
      {(title || subtitle) && (
        <div className="text-center mb-4">
          {title && (
            <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 mb-1">{title}</h3>
          )}
          {subtitle && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{subtitle}</p>
          )}
        </div>
      )}

      <AnimatePresence mode="wait">
        {view === "main" && showEmailCodeStep ? (
          <motion.form
            key="email-code-request"
            name="email-code-request"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            onSubmit={handleRequestEmailCode}
            // Matches the provider column's cap so the two steps line up.
            className="w-full max-w-[400px] mx-auto space-y-3"
          >
            <FormField
              id="code-email"
              label={t("auth.form.email")}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              placeholder={t("auth.form.emailPlaceholder")}
              error={emailError}
              disabled={isLoading}
              autoFocus
            />
            {accountNotFound && onSignUpInstead && (
              <button
                type="button"
                onClick={onSignUpInstead}
                className="w-full py-3 rounded-xl border border-primary-400 text-primary-400 font-semibold hover:bg-primary-500/10 transition-colors"
              >
                {t("auth.signIn.createAccount")}
              </button>
            )}
            {/* The code arm creates the account on verify, so it needs a name
                before sending. The password arm collects it on its own
                sign-up step instead. */}
            {asSignUp && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    id="code-firstName"
                    label={t("auth.form.firstName")}
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={setFirstName}
                    placeholder={t("auth.form.firstNamePlaceholder")}
                    disabled={isLoading}
                  />
                  <FormField
                    id="code-lastName"
                    label={t("auth.form.lastName")}
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={setLastName}
                    placeholder={t("auth.form.lastNamePlaceholder")}
                    disabled={isLoading}
                  />
                </div>
                {nameError && <p className="text-sm text-red-400">{nameError}</p>}
              </>
            )}
            <SubmitButton
              disabled={isLoading}
              loading={isLoading}
              label={t("auth.emailCode.sendCode")}
            />
            <p className="text-center text-sm text-neutral-500 dark:text-neutral-400 pt-1">
              <button
                type="button"
                onClick={() => {
                  clearAllErrors();
                  setShowAllOptions(true);
                }}
                className="text-primary-400 hover:underline font-medium"
              >
                {t("auth.dialog.otherOptions")}
              </button>
            </p>
          </motion.form>
        ) : view === "main" ? (
          <motion.div
            key="main"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            // Capped so the three provider buttons stay a readable column
            // rather than stretching the full width of a desktop dialog.
            className="flex flex-col gap-2.5 w-full max-w-[400px] mx-auto"
          >
            {/* Only reachable via "other options" in the code arm, so it only
                needs a way back there. In control this is the landing view and
                there is nothing behind it. */}
            {emailCodeFlagOn && (
              <BackToMain
                label={t("common.buttons.back")}
                onClick={() => {
                  clearAllErrors();
                  setShowAllOptions(false);
                }}
              />
            )}

            <button
              type="button"
              onClick={handleApple}
              disabled={!!oauthPending}
              className="w-full h-[48px] rounded-full font-semibold text-[15px] flex items-center justify-center gap-2.5 transition-all active:scale-[0.99] disabled:opacity-60"
              style={{
                background: "#ffffff",
                color: "#000000",
                  border: "1px solid rgba(200,200,200,0.4)",
                // boxShadow:
                //   "0 0 0 1px rgba(255,255,255,0.4), 0 8px 20px rgba(0,0,0,0.4)",
              }}
            >
              {oauthPending === "apple" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <AppleLogo className="w-5 h-5" />
              )}
              {asSignUp
                ? t("auth.signUp.withApple")
                : t("auth.signIn.withApple")}
            </button>

            {/* One button for both arms. The redirect arm changes what the tap
                does, not what it looks like — the GIS experiment before it
                swapped in Google's own rendered button, which meant the two
                arms differed in appearance as well as mechanism and no result
                could be attributed to either on its own. */}
            <button
              type="button"
              onClick={handleGoogle}
              disabled={!!oauthPending}
              className="w-full h-[48px] rounded-full font-semibold text-[15px] flex items-center justify-center gap-2.5 transition-all active:scale-[0.99] disabled:opacity-60"
              style={{
                background: "rgba(255, 255, 255)",
                color: "#000000",
                border: "1px solid rgba(200,200,200,0.4)",
                // backdropFilter: "blur(8px)",
              }}
            >
              {oauthPending === "google" ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <GoogleLogo className="w-5 h-5" />
              )}
              {asSignUp
                ? t("auth.signUp.withGoogle")
                : t("auth.signIn.withGoogle")}
            </button>

            <button
              type="button"
              onClick={handleEmailEntry}
              disabled={!!oauthPending}
              // Same treatment as Apple and Google above. It used to be a
              // brand gradient with a glow, which read as the recommended
              // option and pulled the eye away from the two providers most
              // people actually use. The three are equal choices, so they
              // look equal.
              className="w-full h-[48px] rounded-full font-semibold text-[15px] flex items-center justify-center gap-2.5 transition-all active:scale-[0.99] disabled:opacity-60"
              style={{
                background: "#ffffff",
                color: "#000000",
                  border: "1px solid rgba(200,200,200,0.4)",
                // boxShadow:
                //   "0 0 0 1px rgba(255,255,255,0.4), 0 8px 20px rgba(0,0,0,0.4)",
              }}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                {/* currentColor so the glyph tracks the button's text colour
                    — it was hard-coded pale grey for the old dark fill and
                    would be near-invisible on white. */}
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor"><path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280 320-200v-80L480-520 160-720v80l320 200Z"/></svg>
              </span>
              {asSignUp
                ? t("auth.signUp.withEmail")
                : t("auth.signIn.withEmail")}
            </button>

            {error && (
              <p className="mt-2 text-sm text-red-400 text-center">{error}</p>
            )}
          </motion.div>
        ) : view === "email-code-verify" ? (
          <motion.form
            key="email-code-verify"
            name="email-code-verify"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            onSubmit={handleVerifyEmailCode}
            className="space-y-3"
          >
            <BackToMain
              label={t("common.buttons.back")}
              onClick={() => {
                setCode("");
                setCodeResent(false);
                clearAllErrors();
                setView("main");
              }}
            />
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {t("auth.emailCode.subtitle")}{" "}
              <strong className="text-neutral-700 dark:text-neutral-200">
                {email}
              </strong>
            </p>
            <FormField
              id="email-code"
              label={t("auth.emailCode.codeLabel")}
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              // No maxLength: the browser would cut a paste to six characters
              // before we see it, so "123 456\n" lost its last digits. We clean
              // the whole paste ourselves and cap the length in onChange.
              onChange={(v) => setCode(extractEmailCode(v))}
              onPaste={(e) => {
                e.preventDefault();
                setCode(extractEmailCode(e.clipboardData.getData("text")));
              }}
              value={code}
              placeholder={t("auth.emailCode.codePlaceholder")}
              error={codeError}
              disabled={isLoading}
              autoFocus
            />
            <SubmitButton
              disabled={isLoading}
              loading={isLoading}
              label={t("auth.emailCode.submit")}
            />
            {codeResent && (
              <p className="text-sm text-center text-neutral-500 dark:text-neutral-400">
                {t("auth.emailCode.resent")}
              </p>
            )}
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={() => sendEmailCode(true)}
                disabled={isLoading || resendIn > 0}
                className="text-sm text-neutral-500 dark:text-neutral-400 hover:underline disabled:opacity-50 disabled:no-underline"
              >
                {resendIn > 0
                  ? t("auth.emailCode.resendCountdown", { seconds: resendIn })
                  : t("auth.emailCode.resend")}
              </button>
              <button
                type="button"
                onClick={() => switchEmailMethod("password")}
                className="text-sm text-primary-400 hover:underline font-medium"
              >
                {t("auth.emailCode.usePassword")}
              </button>
            </div>
          </motion.form>
        ) : view === "email-code-name" ? (
          <motion.form
            key="email-code-name"
            name="email-code-name"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            onSubmit={handleSubmitCodeName}
            className="space-y-3"
          >
            {/* No back link: the session already exists by this point, so there
                is nothing coherent to go back to. */}
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {t("auth.emailCode.nameSubtitle")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                id="code-name-firstName"
                label={t("auth.form.firstName")}
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={setFirstName}
                placeholder={t("auth.form.firstNamePlaceholder")}
                disabled={isLoading}
              />
              <FormField
                id="code-name-lastName"
                label={t("auth.form.lastName")}
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={setLastName}
                placeholder={t("auth.form.lastNamePlaceholder")}
                disabled={isLoading}
              />
            </div>
            {nameError && <p className="text-sm text-red-400">{nameError}</p>}
            <SubmitButton
              disabled={isLoading}
              loading={isLoading}
              label={t("auth.emailCode.nameSubmit")}
            />
          </motion.form>
        ) : view === "signup-password" ? (
          <motion.form
            key="signup"
            name="signup"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <BackToMain
              label={t("common.buttons.back")}
              onClick={() => {
                clearAllErrors();
                setView("main");
              }}
            />

            <FormField
              id="signup-email"
              label={t("auth.form.email")}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              placeholder={t("auth.form.emailPlaceholder")}
              error={emailError}
              disabled={isLoading}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                id="signup-firstName"
                label={t("auth.form.firstName")}
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={setFirstName}
                placeholder={t("auth.form.firstNamePlaceholder")}
                disabled={isLoading}
              />
              <FormField
                id="signup-lastName"
                label={t("auth.form.lastName")}
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={setLastName}
                placeholder={t("auth.form.lastNamePlaceholder")}
                disabled={isLoading}
              />
            </div>
            {nameError && <p className="text-sm text-red-400">{nameError}</p>}
            <FormField
              id="new-password"
              label={t("auth.form.password")}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={setPassword}
              placeholder={t("auth.form.newPasswordPlaceholder")}
              error={passwordError}
              disabled={isLoading}
            />
            <FormField
              id="confirm-password"
              label={t("auth.form.confirmPassword")}
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder={t("auth.form.confirmPasswordPlaceholder")}
              error={confirmPasswordError}
              disabled={isLoading}
            />
            <SubmitButton
              disabled={isLoading}
              loading={isLoading}
              label={t("auth.signUp.button")}
            />
            {/* Only in the experiment arm: offering the code path to control
                users would contaminate the comparison. QA reaches it with
                ?ff=email_code_auth:experiment. */}
            {emailCodeFlagOn && (
              <p className="text-center text-sm pt-1">
                <button
                  type="button"
                  onClick={() => switchEmailMethod("code")}
                  className="text-primary-400 hover:underline font-medium"
                >
                  {t("auth.emailCode.useCode")}
                </button>
              </p>
            )}
            {allowSwitch && (
              <p className="text-center text-sm text-neutral-500 dark:text-neutral-400 pt-2">
                {t("auth.signUp.hasAccount")}{" "}
                <button
                  type="button"
                  onClick={() => switchView("signin-password")}
                  className="text-primary-400 hover:underline font-medium"
                >
                  {t("auth.signUp.signInInstead")}
                </button>
              </p>
            )}
          </motion.form>
        ) : view === "reset-password" ? (
          <motion.div
            key="reset"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            className="space-y-3"
          >
            <BackToMain
              label={t("common.buttons.back")}
              onClick={() => {
                setResetEmailSent(false);
                clearAllErrors();
                setView("signin-password");
              }}
            />
            {resetEmailSent ? (
              <div className="text-center space-y-3 pt-2">
                <p className="text-neutral-900 dark:text-neutral-50 font-medium">
                  {t("auth.forgotPassword.emailSent")}
                </p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {t("auth.forgotPassword.emailSentMessage")}{" "}
                  <strong className="text-neutral-700 dark:text-neutral-200">{email}</strong>
                </p>
                <SubmitButton
                  disabled={false}
                  loading={false}
                  label={t("auth.forgotPassword.backToSignIn")}
                  onClick={() => {
                    setResetEmailSent(false);
                    setView("signin-password");
                  }}
                />
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-3">
                <FormField
                  id="reset-email"
                  label={t("auth.form.email")}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={setEmail}
                  placeholder={t("auth.form.emailPlaceholder")}
                  error={emailError}
                  disabled={isLoading}
                  autoFocus
                />
                <SubmitButton
                  disabled={isLoading}
                  loading={isLoading}
                  label={t("auth.forgotPassword.sendLink")}
                />
              </form>
            )}
          </motion.div>
        ) : view === "account-not-found" ? (
          <motion.div
            key="account-not-found"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            className="space-y-3"
          >
            <button
              type="button"
              onClick={onAccountNotFoundCreate}
              className="w-full py-3 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-xl transition-colors"
            >
              {t("auth.accountNotFound.createProfile")}
            </button>
            <button
              type="button"
              onClick={handleAccountNotFoundTryOther}
              className="w-full py-3 bg-neutral-700 hover:bg-neutral-600 text-neutral-900 dark:text-neutral-50 font-semibold rounded-xl transition-colors"
            >
              {t("auth.accountNotFound.tryOtherAccount")}
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="signin"
            name="signin"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.18 }}
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <BackToMain
              label={t("common.buttons.back")}
              onClick={() => {
                clearAllErrors();
                setView("main");
              }}
            />
            <FormField
              id="signin-email"
              label={t("auth.form.email")}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              placeholder={t("auth.form.emailPlaceholder")}
              error={emailError}
              disabled={isLoading}
              autoFocus
            />
            <FormField
              id="signin-password"
              label={t("auth.form.password")}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              placeholder={t("auth.form.passwordPlaceholder")}
              error={passwordError}
              disabled={isLoading}
            />
            <SubmitButton
              disabled={isLoading}
              loading={isLoading}
              label={t("auth.signIn.button")}
            />
            {emailCodeFlagOn && (
              <p className="text-center text-sm pt-1">
                <button
                  type="button"
                  onClick={() => switchEmailMethod("code")}
                  className="text-primary-400 hover:underline font-medium"
                >
                  {t("auth.emailCode.useCode")}
                </button>
              </p>
            )}
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setResetEmailSent(false);
                  clearAllErrors();
                  setView("reset-password");
                }}
                className="text-sm text-neutral-500 dark:text-neutral-400 hover:underline"
              >
                {t("auth.form.forgotPassword")}
              </button>
              {allowSwitch && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {t("auth.signIn.noAccount")}{" "}
                  <button
                    type="button"
                    onClick={() => switchView("signup-password")}
                    className="text-primary-400 hover:underline font-medium"
                  >
                    {t("auth.signIn.createAccount")}
                  </button>
                </p>
              )}
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
        >
          <p className="text-sm text-red-400">{error}</p>
        </motion.div>
      )}
    </div>
  );
}

interface FormFieldProps {
  id: string;
  label: string;
  type: "email" | "password" | "text";
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Surfaces the numeric keypad on mobile for the one-time code field. */
  inputMode?: "numeric";
  maxLength?: number;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
}

function BackToMain({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-[11px] tracking-[0.16em] uppercase font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:text-neutral-100 transition-colors mb-2"
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function FormField({
  id,
  label,
  type,
  autoComplete,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  autoFocus,
  inputMode,
  maxLength,
  onPaste,
}: FormFieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="input-label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        inputMode={inputMode}
        maxLength={maxLength}
        onPaste={onPaste}
        className="input-glass"
      />
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

function SubmitButton({
  disabled,
  loading,
  label,
  onClick,
}: {
  disabled: boolean;
  loading: boolean;
  label: string;
  /** Optional click handler — when provided, the button switches to type="button"
   *  so it doesn't submit the surrounding form. */
  onClick?: () => void;
}) {
  return (
    <button
      type={onClick ? "button" : "submit"}
      onClick={onClick}
      disabled={disabled}
      // Solid brand orange, matching the primary CTA everywhere else — the
      // onboarding dock, the plan picker, the booking buttons. The gradient
      // (and the purple glow that came with it) made the one button that
      // costs the user something look unlike every other button they had
      // pressed to get there.
      className="w-full py-3.5 rounded-full font-semibold text-[15px] text-white bg-primary-500 hover:bg-primary-600 flex items-center justify-center gap-2 transition-all active:scale-[0.99] disabled:opacity-60"
    >
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : label}
    </button>
  );
}
